import React, { useState, useRef, useEffect } from "react";
import axios from "axios";
import { useInput, useNotify } from "react-admin";
import { useRecordContext } from "ra-core";
import {
  Button,
  LinearProgress,
  Typography,
  Box,
} from "@material-ui/core";
import CloudUploadIcon from "@material-ui/icons/CloudUpload";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import { makeStyles } from "@material-ui/core/styles";
import { apiUrl } from "../../dataProvider";

const useStyles = makeStyles(() => ({
  wrapper: {
    width: "100%",
    margin: "8px 0 16px",
  },
  dropArea: {
    border: "1px dashed #b89c3a",
    borderRadius: 8,
    padding: 20,
    textAlign: "center",
    background: "#fbf9f2",
  },
  preview: {
    width: "100%",
    maxWidth: 480,
    marginTop: 12,
    borderRadius: 8,
    background: "#000",
  },
  pathBox: {
    marginTop: 8,
    padding: "6px 10px",
    background: "#f1efe9",
    borderRadius: 4,
    fontFamily: "monospace",
    fontSize: 12,
    wordBreak: "break-all",
  },
  success: {
    display: "flex",
    alignItems: "center",
    color: "#2e7d32",
    gap: 6,
    marginTop: 8,
  },
  error: {
    color: "#d32f2f",
    marginTop: 8,
  },
}));

// S3 expects the form fields exactly as returned by the presigned response, with
// the `file` field appended last. Any extra field not present in the policy will
// be rejected, so we only append what the backend gave us plus the file.
const buildS3FormData = (fields, file) => {
  const formData = new FormData();
  Object.keys(fields).forEach(key => formData.append(key, fields[key]));
  formData.append("file", file);
  return formData;
};

// A clean axios instance so the admin Bearer token is NOT sent to S3 (S3 would
// reject the request or it conflicts with the presigned signature).
const createS3Client = () => {
  const client = axios.create();
  if (client.defaults.headers && client.defaults.headers.common) {
    delete client.defaults.headers.common.Authorization;
    delete client.defaults.headers.common.Accept;
  }
  return client;
};

const VideoUploadInput = ({
  source = "video_file_path",
  label = "Video",
  previewUrl,
  ...props
}) => {
  const classes = useStyles();
  const notify = useNotify();
  const fileInputRef = useRef(null);
  const record = useRecordContext(props);
  const {
    input: { value, onChange },
    meta: { touched, error },
  } = useInput({ source, ...props });

  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [localPreview, setLocalPreview] = useState(null);
  const [uploadError, setUploadError] = useState(null);
  const savedPreviewUrl = previewUrl || (record && record.preview_url);
  const videoSrc = localPreview || savedPreviewUrl;

  useEffect(() => {
    return () => {
      if (localPreview) URL.revokeObjectURL(localPreview);
    };
  }, [localPreview]);

  const handleSelectClick = () => {
    if (fileInputRef.current) fileInputRef.current.click();
  };

  const handleFileChange = async event => {
    const file = event.target.files && event.target.files[0];
    // reset the input so selecting the same file again re-triggers change
    event.target.value = "";
    if (!file) return;

    setUploadError(null);
    setProgress(0);

    const objectUrl = URL.createObjectURL(file);
    setLocalPreview(prev => {
      if (prev) URL.revokeObjectURL(prev);
      return objectUrl;
    });

    setUploading(true);
    try {
      // 1. Ask our backend for a presigned POST policy.
      const { data: presigned } = await axios.post(
        `${apiUrl}/training-videos/generate-presigned-url`,
        { filename: file.name }
      );

      const { url, fields, key } = presigned;

      // 2. Upload the file straight to S3 with progress tracking.
      const s3Client = createS3Client();
      await s3Client.post(url, buildS3FormData(fields, file), {
        onUploadProgress: progressEvent => {
          if (progressEvent.total) {
            setProgress(
              Math.round((progressEvent.loaded * 100) / progressEvent.total)
            );
          }
        },
      });

      // 3. Store the returned key as the form value (this is what gets saved).
      onChange(key);
      notify("Video uploaded successfully", "info");
    } catch (err) {
      const message =
        (err.response && err.response.data && err.response.data.message) ||
        "Video upload failed. Please try again.";
      setUploadError(message);
      notify(message, "warning");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className={classes.wrapper}>
      <Typography variant="subtitle2" gutterBottom>
        {label}
      </Typography>

      <div className={classes.dropArea}>
        <input
          ref={fileInputRef}
          type="file"
          accept="video/*"
          style={{ display: "none" }}
          onChange={handleFileChange}
        />
        <Button
          variant="contained"
          color="primary"
          startIcon={<CloudUploadIcon />}
          onClick={handleSelectClick}
          disabled={uploading}
        >
          {value ? "Replace Video" : "Upload Video"}
        </Button>
        <Typography variant="caption" display="block" style={{ marginTop: 8 }}>
          The file is uploaded directly to S3. Click Save once the upload finishes.
        </Typography>
      </div>

      {uploading && (
        <Box mt={1}>
          <LinearProgress variant="determinate" value={progress} />
          <Typography variant="caption">{progress}% uploaded</Typography>
        </Box>
      )}

      {!uploading && value && (
        <div className={classes.success}>
          <CheckCircleIcon fontSize="small" />
          <Typography variant="body2">Video ready</Typography>
        </div>
      )}

      {value && <div className={classes.pathBox}>{value}</div>}

      {videoSrc && (
        <video className={classes.preview} src={videoSrc} controls />
      )}

      {uploadError && (
        <Typography variant="body2" className={classes.error}>
          {uploadError}
        </Typography>
      )}

      {touched && error && !value && (
        <Typography variant="body2" className={classes.error}>
          {error}
        </Typography>
      )}
    </div>
  );
};

export default VideoUploadInput;
